import { useTranslation } from 'react-i18next';

import { IconType, Icons } from '../../../../constants/icons';
import { AttachmentImage } from '../../../../models/Base';
import { UserInvitationType } from '../../../../models/UserStore/Invitation';
import { Avatar } from '../../../../models/UserStore/Profile';

import {
  AvatarNode,
  Card,
  CountDownText,
  InvitationStepsButtonGroup,
  SeparatorComponent,
} from '../../../Molecules';

import { getInvitationMessage } from '../../../../constants/shareInvitationLink';
import './ShareTemplate.css';

export type UserViewProps = {
  userName: string | undefined;
  userFollowerCount: number;
  userId: string;
  userAvatar: Avatar | undefined;
  userProfilePicture: AttachmentImage | null | undefined;
  userVerified?: boolean;
};

type ShareTemplateProps = {
  type: UserInvitationType;
  contentInviter: Omit<UserViewProps, 'userFollowerCount'>;

  //leaderboardTeamInvitation
  leaderboardTeamInvitationData: {
    team: { teamIcon: string | undefined; teamName: string | undefined };
    leaderboard: {
      name: string | undefined;
      description: string | null | undefined;
      endedAt: Date | undefined;
      memberCount: number | null | undefined;
      rewardOfferCount: number | null | undefined;
      albumCount: number | null | undefined;
    };
    space: { spaceIcon: string | undefined; spaceName: string | undefined };
  };

  referralQuotaLeft: number;
  expiryAt: Date;

  onClickPrimary: () => void;
};

export const ShareTemplate = (props: ShareTemplateProps) => {
  const {
    type,
    contentInviter,

    leaderboardTeamInvitationData,

    // referralQuotaLeft,
    expiryAt,
    onClickPrimary,
  } = props;

  const { t } = useTranslation();

  const invitationMessage = getInvitationMessage(type);

  return (
    <div
      className="flex flex-col share-content"
      style={{
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
      }}
    >
      <Card style={{ width: '100%', maxWidth: '480px' }}>
        <AvatarNode
          avatar={{
            ...contentInviter.userAvatar,
            userId: contentInviter.userId,
            source:
              contentInviter.userProfilePicture &&
              contentInviter.userProfilePicture.publicUrls?.small
                ? { uri: contentInviter.userProfilePicture.publicUrls?.small }
                : '',
          }}
          size={80}
        />

        <p
          className="text-lg text-cyan-800"
          style={{ textAlign: 'center', padding: '1rem' }}
        >
          <span
            className="font-bold"
            style={{
              wordBreak: 'break-all',
            }}
          >
            {contentInviter.userName}{' '}
          </span>
          {t(invitationMessage)}
        </p>
        <>
          {type == UserInvitationType.LeaderboardTeamInvitation ? (
            <div
              className="flex flex-col items-center"
              style={{ gap: '0.75rem', width: '100%', paddingBottom: '1.5rem' }}
            >
              <p className="font-bold">{t('Team')}</p>
              <Card
                style={{
                  backgroundColor: '#f2f2f2',
                  flexDirection: 'row',
                  gap: '0.5rem',
                  width: '100%',
                  justifyContent: 'flex-start',
                }}
              >
                <img
                  src={leaderboardTeamInvitationData.team.teamIcon}
                  style={{
                    width: '48px',
                    aspectRatio: 1,
                    borderRadius: '50%',
                    border: '1px solid #cccccc50',
                  }}
                />
                <p className="font-bold">
                  {leaderboardTeamInvitationData.team.teamName}
                </p>
              </Card>

              <p className="font-bold">{t('Path Leaderboard')}</p>
              <Card
                style={{
                  backgroundColor: '#f2f2f2',
                  gap: '0.5rem',
                  width: '100%',
                }}
              >
                <div className="flex flex-row gap-2 w-full">
                  <img
                    className="size-icon-xl"
                    src={Icons[IconType.CircleCompetition]}
                  />
                  <div className="flex flex-col" style={{ gap: '0.25rem' }}>
                    <p>{leaderboardTeamInvitationData.leaderboard.name}</p>
                    <p className="text-sm">
                      {leaderboardTeamInvitationData.leaderboard.description}
                    </p>
                    <div className="text-sm text-red-550">
                      {leaderboardTeamInvitationData.leaderboard.endedAt ? (
                        <div className="flex flex-row items-center gap-1">
                          <img
                            className="size-icon-s"
                            src={Icons[IconType.TimeLeft]}
                          />
                          <CountDownText
                            timestamp={leaderboardTeamInvitationData.leaderboard.endedAt.getTime()}
                            label={t('End: ')}
                            className="countdowntext text-sm"
                          />
                        </div>
                      ) : null}
                    </div>
                    <SeparatorComponent
                      separator={<p className="text-gray-600 text-sm">·</p>}
                    >
                      {leaderboardTeamInvitationData.leaderboard.memberCount ? (
                        <p className="text-gray-600 text-sm">
                          {t('{count} Members', {
                            count:
                              leaderboardTeamInvitationData.leaderboard
                                .memberCount,
                          })}
                        </p>
                      ) : null}
                      {leaderboardTeamInvitationData.leaderboard.albumCount ? (
                        <p className="text-gray-600 text-sm">
                          {t('{count} Paths', {
                            count:
                              leaderboardTeamInvitationData.leaderboard
                                .albumCount,
                          })}
                        </p>
                      ) : null}
                      {leaderboardTeamInvitationData.leaderboard
                        .rewardOfferCount ? (
                        <p className="text-gray-600 text-sm">
                          {t('{count} Rewards', {
                            count:
                              leaderboardTeamInvitationData.leaderboard
                                .rewardOfferCount,
                          })}
                        </p>
                      ) : null}
                    </SeparatorComponent>
                  </div>
                </div>
                <hr />
                <div
                  className="flex flex-row items-center gap-2"
                  style={{
                    width: '100%',
                    justifyContent: 'flex-start',
                  }}
                >
                  <img
                    src={leaderboardTeamInvitationData.space.spaceIcon}
                    style={{
                      width: '32px',
                      aspectRatio: 1,
                      borderRadius: '0.5rem',
                      border: '1px solid #cccccc50',
                    }}
                  />
                  <p className="text-gray-600">
                    {leaderboardTeamInvitationData.space.spaceName}
                  </p>
                </div>
              </Card>
            </div>
          ) : null}
          {/* {(post && allowDisplay && type == UserInvitationType.PostSharing) ||
          type == UserInvitationType.GameSetSharing ||
          type == UserInvitationType.GameSharing ||
          (sharingUser && type == UserInvitationType.UserSharing) ? (
            <UserSharingResponseActionGroup
              type={type}
              onPress={onClickPrimary}
            />
          ) : null} */}
          {/* {type == UserInvitationType.ClassroomSharing ||
          type == UserInvitationType.LeaderboardTeamInvitation ||
          type == UserInvitationType.MultiplayerGameSessionInvitation ||
          type == UserInvitationType.FriendChallengeInvitation ? ( */}
          <div
            className="flex flex-col items-center w-full"
            style={{ padding: '0.25rem', marginTop: '16px' }}
          >
            {/* show quota */}
            {/* {type == UserInvitationType.ClassroomSharing ||
              type == UserInvitationType.MultiplayerGameSessionInvitation ? (
                <>
                  <div className="flex flex-row items-center gap-1">
                    <img
                      src={Icons[IconType.Referral]}
                      className="size-icon-xl"
                    />
                    <p className="text-3xl font-bold">{referralQuotaLeft}</p>
                  </div>
                  <p>{t('Referral Quota Left')}</p>

                  {type == UserInvitationType.ClassroomSharing ? (
                    <div
                      style={{ textAlign: 'center' }}
                      className="text-gray-600"
                    >
                      <Trans
                        i18nKey="User_Follow_Club_Reward"
                        className="flex flex-row items-center "
                      >
                        <img
                          src={Icons[IconType.JCoin]}
                          className="size-icon-m"
                          style={{
                            transform: 'translateY(-2px)',
                          }}
                        />
                      </Trans>
                    </div>
                  ) : null}
                </>
              ) : null}

              <hr /> */}
            {/* <p className="text-lg font-bold">{t('Click OK to view details')}</p>
            <TextButton
              text={t('OK')}
              size={TextButtonSize.Large}
              variant={TextButtonVariant.Cyan}
              onClick={onClickPrimary}
            /> */}
            <InvitationStepsButtonGroup onPressAccept={onClickPrimary} />

            <CountDownText
              timestamp={expiryAt.getTime()}
              label={t('Expiry: ')}
              className="text-sm font-bold text-red-550"
            />
          </div>
          {/* ) : null} */}
        </>
      </Card>
    </div>
  );
};
